/**
 * OFTEC Oil Firing Calculations & Tank Distance Compliance Engine
 *
 * Per OFTEC (Oil Firing Technical Association), BS 5410 Parts 1 & 2,
 * and Building Regulations Approved Document J.
 *
 * Provides:
 *  - Combustion efficiency (Net & Gross) from excess O₂ / CO₂ / flue temp
 *  - Excess air % from O₂ reading
 *  - CO ppm / smoke number thresholds
 *  - Oil storage tank separation / fire barrier compliance (BS 5410)
 *  - Fire valve & oil supply line checks
 */

/**
 * Smoke number for pump (OFTEC / BS 5410 limit).
 * Smoke number 0-1 is normal; > 1 indicates soot build-up (At Risk).
 */
export const SMOKE_NUMBER_LIMIT = 1;
export const TANK_SEPARATION_BUILDING_MIN_M = 1.8;
export const TANK_SEPARATION_BOUNDARY_MIN_MM = 760;

/**
 * Fuels: Flue gas CO₂ reference values & calorific data.
 * Values are representative for kerosene (28 sec) and gas oil (35 sec).
 */
export const FUEL_PROPERTIES = {
  kerosene28: {
    name: 'Kerosene (28 sec)',
    netCvMJPerKg: 43.1,
    grossCvMJPerKg: 45.8,
    co2MaxPct: 15.6
  },
  gasOil35: {
    name: 'Gas Oil (35 sec)',
    netCvMJPerKg: 42.6,
    grossCvMJPerKg: 45.4,
    co2MaxPct: 15.4
  }
};

/**
 * Calculate excess air % from O₂ reading.
 * Excess Air % ≈ (O₂% / (21 − O₂%)) × 100
 *
 * @param {number} o2Pct - Measured oxygen in flue gas (%)
 * @returns {number}
 */
export const calculateExcessAir = (o2Pct = 0) => {
  const o2 = Number(o2Pct) || 0;
  if (o2 <= 0 || o2 >= 21) return 0;
  return Number(((o2 * 100) / (21 - o2)).toFixed(1));
};

/**
 * Estimate combustion efficiency from measured O₂/CO₂ and flue gas temperature.
 * Uses the Siegert-type dry flue gas loss approximation (per combustion analyser
 * convention for liquid fuels):
 *   Loss% = (FlueGasTemp − AmbientTemp) × (A / (21 − O₂))
 * where A ≈ 0.7 for kerosene / gas oil.
 *   Efficiency% = 100 − Loss%
 *
 * @param {Object} opts
 * @param {number} opts.o2Pct Measured oxygen in flue gas (%) – primary input
 * @param {number} [opts.co2Pct] Optional measured CO₂ % (used for reference)
 * @param {number} opts.flueTempC Flue gas outlet temperature °C
 * @param {number} [opts.ambientTempC=20] Ambient air temperature °C
 * @param {number} [opts.siegertA=0.7] Siegert coefficient for the fuel (oil ≈ 0.7)
 * @returns {Object} { netEfficiencyPct, grossEfficiencyPct, heatLossPct, excessAir, o2Pct, co2Pct }
 */
export const calculateCombustionEfficiency = ({
  o2Pct = 0,
  co2Pct = 0,
  flueTempC = 0,
  ambientTempC = 20,
  siegertA = 0.7
}) => {
  const o2 = Number(o2Pct) || 0;
  const co2 = Number(co2Pct) || 0;
  const flueTemp = Number(flueTempC) || 0;
  const ambient = Number(ambientTempC) || 20;
  const aCoef = Number(siegertA) || 0.7;

  // Dry flue loss — Siegert convention (per-cent)
  let heatLossPct = 0;
  if (o2 >= 0 && o2 < 21 && flueTemp > ambient) {
    heatLossPct = (flueTemp - ambient) * (aCoef / (21 - o2));
  }

  const netEfficiencyPct = Number(Math.max(0, 100 - heatLossPct).toFixed(1));
  const grossEfficiencyPct = Number(Math.max(0, netEfficiencyPct - 8).toFixed(1)); // ~8% difference gross v net for oil

  return {
    netEfficiencyPct,
    grossEfficiencyPct,
    heatLossPct: Number(heatLossPct.toFixed(1)),
    excessAir: calculateExcessAir(o2),
    o2Pct: o2,
    co2Pct: co2,
    flueTempC: flueTemp,
    ambientTempC: ambient
  };
};

/**
 * Evaluate combustion test result against typical good-practice thresholds.
 *
 * @param {Object} opts
 * @param {number} [opts.smokeNumber=0] Smoke pump test number (0-9)
 * @param {number} [opts.coPpm=0] Carbon monoxide ppm
 * @param {number} [opts.netEfficiencyPct=90] Net efficiency %
 * @param {number} [opts.co2Pct=0] CO₂ %
 * @returns {Object} { pass, smokeNumber, smokeNote, coPpm, coNote, efficiencyNote, issues }
 */
export const evaluateCombustionTest = ({
  smokeNumber = 0,
  coPpm = 0,
  netEfficiencyPct = 90,
  co2Pct = 0
}) => {
  const smoke = Number(smokeNumber) || 0;
  const co = Number(coPpm) || 0;
  const eff = Number(netEfficiencyPct) || 0;

  const issues = [];

  // Smoke test (0-1 is a normal range)
  if (smoke > SMOKE_NUMBER_LIMIT) {
    issues.push(`Smoke number ${smoke} exceeds the recommended maximum of ${SMOKE_NUMBER_LIMIT} for a clean burn. Investigate nozzle/fuel condition.`);
  }

  // CO guidance (draft tight)
  if (co > 100) {
    issues.push(`CO reading of ${co} ppm is high for an oil-fired appliance. Check air shutter, nozzle and combustion setting.`);
  }

  if (eff < 80) {
    issues.push(`Net combustion efficiency of ${eff}% is below the 80% reference minimum. Check for excess air and heat exchanger fouling.`);
  }

  return {
    pass: issues.length === 0,
    smokeNumber: smoke,
    smokeNote: smoke <= 1 ? 'Pass – smoke within limit' : `At Risk – smoke number ${smoke}`,
    coPpm: co,
    coNote: co <= 100 ? 'Pass – CO within limit' : `At Risk – CO ${co} ppm`,
    netEfficiencyPct: eff,
    fuel: co2Pct,
    issues
  };
};

/**
 * BS 5410 oil storage tank separation distance compliance.
 *
 * @param {Object} opts
 * @param {number} opts.distanceToBuildingM - Distance to nearest building / combustible structure (m)
 * @param {number} opts.distanceToBoundaryM - Distance to site boundary (m)
 * @param {number} [opts.distanceToFixedSourceM=2] - To non-fire rated boundary
 * @param {number} [opts.capacityLitres=1200] - Tank capacity (litres). Threshold 2500 triggers full BS 5410 large storage design.
 * @param {boolean} [opts.fireScreenFitted=false] - Whether a fire protection screen is fitted
 * @returns {Object} { compliant, issues, distanceToBuildingM, distanceToBoundaryM }
 */
export const validateTankSeparation = ({
  distanceToBuildingM = 0,
  distanceToBoundaryM = 0,
  distanceToFireScreenBoundaryM = 2,
  capacityLitres = 1200,
  fireScreenFitted = false
}) => {
  const issues = [];
  const buildDistance = Number(distanceToBuildingM) || 0;
  const boundaryDistance = Number(distanceToBoundaryM) || 0;

  // BS 5410: 1.8 m from buildings / combustibles / non fire-rated boundary
  if (buildDistance < TANK_SEPARATION_BUILDING_MIN_M) {
    if (fireScreenFitted && buildDistance >= TANK_SEPARATION_BOUNDARY_MIN_MM / 1000) {
      // fire screen allows reduced separation (min 0.76 m)
      issues.push(`Tank separation of ${buildDistance}m with fitted fire protection screen is acceptable (minimum 0.76m).`);
    } else {
      issues.push(`Tank is ${buildDistance}m from building, below BS 5410 minimum of ${TANK_SEPARATION_BUILDING_MIN_M}m. Install fire protection screen or reposition.`);
    }
  }

  // Boundary separation 760 mm
  if (boundaryDistance < TANK_SEPARATION_BOUNDARY_MIN_MM / 1000) {
    issues.push(`Tank is ${boundaryDistance}m from boundary, below the ${TANK_SEPARATION_BOUNDARY_MIN_MM}mm minimum.`);
  }

  // Large above-ground storage >2500 litres requires bundled / above-ground tank standard
  if (Number(capacityLitres) > 2500) {
    issues.push(`Capacity ${capacityLitres} litres exceeds 2500 L – require fully compliant bunding and OFTEC/Building Regs A.D.J design.`);
  }

  return {
    compliant: issues.length === 0,
    distanceToBuildingM: buildDistance,
    distanceToBoundaryM: boundaryDistance,
    capacityLitres: Number(capacityLitres) || 0,
    fireScreenFitted,
    issues
  };
};

/**
 * Fire valve remote sensing check
 * OFTEC requires a remote sensing fire valve with a sensor at or near the burner.
 * @param {number} tripTemperatureC Measured trip temperature °C
 * @returns {Object} { correct, optimumTempC, reading, issues }
 */
export const validateFireValveTrip = (tripTemperatureC = 0) => {
  const reading = Number(tripTemperatureC) || 0;
  const optimum = 99; // deg C typically on oil line valve
  const issues = [];
  if (reading > 0 && (reading < 48 || reading > 150)) {
    issues.push(`Fire valve trip temperature ${reading}°C is outside the expected 48–150°C range.`);
  }
  return {
    valid: issues.length === 0,
    optimumTripC: optimum,
    readingC: reading,
    issues
  };
};

export default {
  SMOKE_NUMBER_LIMIT,
  FUEL_PROPERTIES,
  calculateExcessAir,
  calculateCombustionEfficiency,
  evaluateCombustionTest,
  validateTankSeparation,
  validateFireValveTrip
};